// Display geometry only. The sourceBlocks and their original OCR rectangles
// remain untouched; these bounds are never persisted as source coordinates.
export const citationBbox = value => {
    const box = {left: Number(value?.left ?? value?.Left), top: Number(value?.top ?? value?.Top),
        width: Number(value?.width ?? value?.Width), height: Number(value?.height ?? value?.Height)};
    return Object.values(box).every(Number.isFinite) && box.left >= 0 && box.top >= 0
        && box.width > 0 && box.height > 0 && box.left + box.width <= 1.000001
        && box.top + box.height <= 1.000001 ? box : null;
};

const union = blocks => {
    const boxes = blocks.map(block => citationBbox(block.bbox));
    const left = Math.min(...boxes.map(box => box.left));
    const top = Math.min(...boxes.map(box => box.top));
    return {left, top, width: Math.max(...boxes.map(box => box.left + box.width)) - left,
        height: Math.max(...boxes.map(box => box.top + box.height)) - top};
};

const neighboringLines = (previous, next) => {
    const a = citationBbox(previous.bbox), b = citationBbox(next.bbox);
    const lineHeight = Math.min(a.height, b.height);
    const gap = Math.max(0, b.top - a.top - a.height);
    const overlap = Math.max(0, Math.min(a.left + a.width, b.left + b.width) - Math.max(a.left, b.left));
    const aligned = Math.abs(a.left - b.left) <= .035 || Math.abs(a.left + a.width - b.left - b.width) <= .035;
    // A blank paragraph gap or a different column starts another outline.
    return gap <= lineHeight * .7 && overlap / Math.min(a.width, b.width) >= .55 && aligned;
};

export const clusterCitationHighlights = blocks => {
    const groups = new Map();
    blocks.forEach(block => {
        if (!block?.ref || !citationBbox(block.bbox)) return;
        const key = JSON.stringify([block.file_id ?? null, block.document_version_id ?? null, block.page,
            block.ref, block.source_citation_id || block.citation_group_id || block.ref]);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(block);
    });
    return [...groups.entries()].flatMap(([groupKey, entries]) => {
        const clusters = [];
        [...entries].sort((a, b) => citationBbox(a.bbox).top - citationBbox(b.bbox).top
            || citationBbox(a.bbox).left - citationBbox(b.bbox).left).forEach(block => {
            // Find the neighbouring column's last line, not an enclosing union
            // whose growing height could bridge unrelated paragraphs.
            const cluster = [...clusters].reverse().find(items => neighboringLines(items[items.length - 1], block));
            if (cluster) cluster.push(block);
            else clusters.push([block]);
        });
        return clusters.map((sourceBlocks, index) => ({...sourceBlocks[0],
            bbox: union(sourceBlocks), sourceBlocks, displayClusterKey: `${groupKey}:${index}`}));
    }).sort((a, b) => a.page - b.page || a.bbox.top - b.bbox.top || a.bbox.left - b.bbox.left);
};
